<template>
  <div class="module-rules__area">
    <div class="module-rules__criteria">
      <div class="module-rules__caption">Criteria:</div>
      <slot name="criteria" />
      <RulesFormSettingsCriteria
        v-for="(name, index) in criteriaList"
        :key="name"
        v-model="criteriaModel"
        :name
        :index
        :prev-criteria
        :disabled
      />
    </div>
    <div class="module-rules__actions">
      <slot
        name="actions"
        :actions="actionsModel"
        :update-actions="updateActions"
      />
    </div>
  </div>
  <div class="module-rules__buttons">
    <slot name="before" />
    <div />
    <slot name="after" />
  </div>
</template>

<script setup lang="ts" generic="T">
import { computed, onMounted, provide, ref } from 'vue'
import { cloneDeep } from 'lodash'

import { TransactionRuleCriteria, TransactionRuleCriteriaType } from '@types'

import { CONTROL_SIZE } from './utils/const'

import RulesFormSettingsCriteria from './RulesFormSettingsCriteria.vue'

type Props = {
  mapCriteria?: (criteria: TransactionRuleCriteriaType[]) => TransactionRuleCriteriaType[]
  disabled?: boolean
}

const props = defineProps<Props>()

const criteriaModel = defineModel<TransactionRuleCriteria[]>('criteriaModel', {
  required: true,
})
const actionsModel = defineModel<T>('actionsModel', {
  required: true,
})

provide('size', CONTROL_SIZE)

const prevCriteria = ref<TransactionRuleCriteria[]>([])

const criteriaList = computed(() => {
  const criteria = Object.values(TransactionRuleCriteriaType)
  return props.mapCriteria ? props.mapCriteria(criteria) : criteria
})

const updateActions = (actions: T) => {
  actionsModel.value = actions
}

onMounted(() => {
  prevCriteria.value = cloneDeep(criteriaModel.value)
})
</script>

<style>
.module-rules {
  &__area {
    @apply flex-auto;
    @apply grid grid-cols-1 sm:grid-cols-2 gap-4;
    @apply px-4 md:px-6 pb-4;
  }

  &__caption {
    @apply sticky top-0;
    @apply grid grid-cols-1 sm:grid-cols-2;
    @apply py-2;
    @apply bg-gray-100 dark:bg-gray-800;
    @apply text-gray-950 dark:text-gray-200;
    @apply text-sm sm:text-xs font-medium tracking-wide uppercase;
    @apply z-10;
  }

  &__criteria,
  &__actions {
    @apply flex flex-col;
    @apply gap-4;
  }

  &__criteria &__caption {
    @apply -mx-4 sm:-mr-2 md:-ml-6;
    @apply pl-4 md:pl-6;
  }

  &__actions &__caption {
    @apply -mx-4 sm:-ml-2 md:-mr-6;
    @apply px-4 sm:pl-2 md:pr-6;
  }

  &__buttons {
    @apply sticky bottom-0;
    @apply flex flex-col sm:flex-row items-center justify-end;
    @apply gap-2 sm:gap-4;
    @apply p-4 md:px-6 pt-2;
    @apply bg-white dark:bg-gray-800;
    @apply z-10;

    > .ui-button {
      @apply w-full sm:w-auto;
      @apply shrink-0;
    }

    .ui-checkbox {
      @apply items-center;
      @apply gap-1.5 py-2 sm:py-0;

      &__label {
        @apply sm:whitespace-nowrap text-xs;
      }
    }
  }
}
</style>
